import React, { useState, useEffect, useCallback } from "react";
import { Paper, Typography, IconButton, TablePagination, Tooltip } from "@mui/material";
import {
  format,
  startOfMonth,
  endOfMonth,
  isSameDay,
  addMonths,
  subMonths,
  addDays,
  startOfWeek,
  isYesterday,
  isTomorrow,
  isSameMonth,
} from "date-fns";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import ru from "date-fns/locale/ru";
import api from "../../Utils/Api";
import { useCookies } from "react-cookie";
import "./Calendar.css";

const eventTypes = [
  { id: "live_lesson", show: true, title: "Живой урок", color: "#6367bf" },
  { id: "test_opened", show: false, title: "Открытие теста", color: "#ab2d2d" },
  {
    id: "deadline_test",
    show: false,
    title: "Дедлайн по тесту",
    color: "#ab2d2d",
  },
  {
    id: "individual_lesson",
    show: true,
    title: "Индивидуальный урок",
    color: "#22C532",
  },
  {
    id: "teacher_reminder",
    show: true,
    title: "Напоминание",
    color: "#7A5AF8",
  },
  {
    id: "group_course_assigned",
    show: false,
    title: "Назначение курса",
    color: "#ab2d2d",
  },
  { id: "video_stream", show: true, title: "Видео стрим", color: "#ab2d2d" },
];

function formatDate(date) {
  return format(date, "yyyy-MM-dd HH:mm:ss");
}

const Calendar = () => {
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dailyEvents, setDailyEvents] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [cookies] = useCookies(["token"]);
  const [page, setPage] = useState(0);
  const rowsPerPage = 5;

  const fetchEvents = useCallback(
    async (start, end) => {
      try {
        const res = await api.getEvents(cookies.token, formatDate(start), formatDate(end));
        const data = res.map((ev) => {
          const evtype = eventTypes.find((event) => event.id === ev.event_type);
          return {
            event_id: ev.id,
            title: ev.event_name,
            eventComment: ev.comment,
            user_id: ev.students_ids,
            start: new Date(ev.start_datetime || ev.end_datetime),
            end: new Date(ev.end_datetime || ev.start_datetime),
            eventType: ev.event_type,
            color: evtype ? evtype.color : "#000000",
          };
        });
        setEvents(data);
      } catch (error) {
        console.error("Ошибка при загрузке событий:", error);
      }
    },
    [cookies.token]
  );

  useEffect(() => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    fetchEvents(start, end);
  }, [currentMonth, cookies.token, fetchEvents]);

  useEffect(() => {
    const todayEvents = events.filter((event) => isSameDay(event.start, selectedDate));
    setDailyEvents(todayEvents);
    setPage(0);
  }, [selectedDate, events]);

  const handleDateClick = (day) => {
    setSelectedDate(day);
  };

  const renderCalendar = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
    const endDate = endOfMonth(monthEnd);

    const dateFormat = "d";
    const rows = [];

    let days = [];
    let day = startDate;
    let formattedDate = "";

    const weekDays = ["П", "В", "С", "Ч", "П", "С", "В"];

    const weekDaysRow = (
      <div className="custom-calendar__weekdays">
        {weekDays.map((weekDay) => (
          <div key={weekDay} className="custom-calendar__weekday">
            {weekDay}
          </div>
        ))}
      </div>
    );

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        formattedDate = format(day, dateFormat);
        const cloneDay = day;
        const hasEvents = events.some((event) => isSameDay(event.start, cloneDay));
        days.push(
          <div
            className={`custom-calendar__day ${
              !isSameMonth(day, monthStart)
                ? "disabled"
                : isSameDay(day, selectedDate)
                ? "selected"
                : ""
            } ${hasEvents ? "has-events" : ""}`}
            key={day}
            onClick={() => handleDateClick(cloneDay)}
          >
            <span className="number">{formattedDate}</span>
          </div>
        );
        day = addDays(day, 1);
      }
      rows.push(
        <div className="custom-calendar__days" key={day}>
          {days}
        </div>
      );
      days = [];
    }
    return (
      <div className="custom-calendar__body">
        {weekDaysRow}
        {rows}
      </div>
    );
  };

  const nextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  const prevMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  const renderDateHeader = () => {
    const today = new Date();
    let headerText = "";

    if (isSameDay(selectedDate, today)) {
      headerText = `Сегодня, <b>${format(selectedDate, "d MMMM", {
        locale: ru,
      })}</b>`;
    } else if (isTomorrow(selectedDate)) {
      headerText = `Завтра, <b>${format(selectedDate, "d MMMM", {
        locale: ru,
      })}</b>`;
    } else if (isYesterday(selectedDate)) {
      headerText = `Вчера, <b>${format(selectedDate, "d MMMM", {
        locale: ru,
      })}</b>`;
    } else {
      headerText = `<b>${format(selectedDate, "d MMMM", { locale: ru })}</b>`;
    }

    return (
      <Typography
        variant="h6"
        className="custom-calendar__events-title"
        sx={{ fontSize: "17px", fontWeight: 700, fontFamily: "Montserrat" }}
      >
        <span dangerouslySetInnerHTML={{ __html: headerText }} />
      </Typography>
    );
  };

  const renderTableRows = () => {
    const sortedEvents = dailyEvents.sort((a, b) => a.start - b.start);
    const eventsToShow = sortedEvents.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    const rows = new Array(rowsPerPage).fill(null);

    eventsToShow.forEach((event, index) => {
      rows[index] = event;
    });

    const eventTime = (event) => {
      const start = format(event.start, "HH:mm");
      const end = format(event.end, "HH:mm");
      if (start != end) {
        return `${start} - ${end}`;
      } else {
        return `${start}`;
      }
    };

    return rows.map((event, index) => (
      <tr key={event ? event.event_id : `empty-${index}`} className="custom-calendar__table-row">
        <td className="custom-calendar__table-cell">{event ? `${eventTime(event)}` : ""}</td>
        <Tooltip
          title={event && event.title.length > 29 ? event.title : ""}
          placement="top-start"
          followCursor
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: "#ffffff",
                "box-shadow": "0px 4px 20px 0px #0000001F",
                color: "#464646",
                "font-size": "17px",
                "line-height": "20px",
                "border-radius": "10px",
                fontFamily: "Montserrat",
                fontWeight: 400,
                padding: "10px",
                "& .MuiTooltip-arrow": {
                  color: "black",
                },
              },
            },
          }}
        >
          <td className="custom-calendar__table-cell">{event ? event.title : ""}</td>
        </Tooltip>
        <Tooltip
          title={event && event.eventComment.length > 29 ? event.eventComment : ""}
          placement="top-start"
          followCursor
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: "#ffffff",
                "box-shadow": "0px 4px 20px 0px #0000001F",
                color: "#464646",
                "font-size": "17px",
                "line-height": "20px",
                "border-radius": "10px",
                fontFamily: "Montserrat",
                fontWeight: 400,
                padding: "10px",
                "& .MuiTooltip-arrow": {
                  color: "black",
                },
              },
            },
          }}
        >
          <td className="custom-calendar__table-cell">{event ? event.eventComment : ""}</td>
        </Tooltip>
        <Tooltip
          title={event && event.eventType.length > 29 ? event.eventType : ""}
          placement="top-start"
          followCursor
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: "#ffffff",
                "box-shadow": "0px 4px 20px 0px #0000001F",
                color: "#464646",
                "font-size": "17px",
                "line-height": "20px",
                "border-radius": "10px",
                fontFamily: "Montserrat",
                fontWeight: 400,
                padding: "10px",
                "& .MuiTooltip-arrow": {
                  color: "black",
                },
              },
            },
          }}
        >
          <td className="custom-calendar__table-cell">
            {event ? eventTypes.find((et) => et.id === event.eventType)?.title || "Неизвестно" : ""}
          </td>
        </Tooltip>
      </tr>
    ));
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  return (
    <div className="custom-calendar__container wrapper">
      <div className='tab__wrap'>
            <div className='tab__btntrue'>Календарь</div>
          </div>
      <Paper elevation={0} className="custom-calendar__calendar">
        <div className="custom-calendar__header">
          <Typography
            variant="h6"
            sx={{
              fontSize: "17px",
              fontWeight: 700,
              fontFamily: "Montserrat",
              marginLeft: "10px",
            }}
          >
            {capitalizeFirstLetter(format(currentMonth, "LLLL yyyy", { locale: ru }))}
          </Typography>
          <div className="custom-calendar__headerBtn">
            <IconButton onClick={prevMonth} sx={{ backgroundColor: "#eff0f5" }}>
              <ChevronLeft />
            </IconButton>
            <IconButton onClick={nextMonth} sx={{ backgroundColor: "#eff0f5" }}>
              <ChevronRight />
            </IconButton>
          </div>
        </div>
        {renderCalendar()}
      </Paper>
      <Paper
        elevation={0}
        className="custom-calendar__events"
        sx={{
          maxWidth: "60vw",
          padding: 0,
          "@media (min-width: 1600px)": {
            maxWidth: "63vw",
          },
          "@media (min-width: 1700px)": {
            maxWidth: "65vw",
          },
        }}
      >
        {renderDateHeader()}
        <div className="custom-calendar__table-container">
          <table className="custom-calendar__table">
            <thead>
              <tr>
                <th className="custom-calendar__table-header" style={{ width: "120px" }}>
                  Время
                </th>
                <th className="custom-calendar__table-header">Название</th>
                <th className="custom-calendar__table-header">Описание</th>
                <th className="custom-calendar__table-header">Тип</th>
              </tr>
            </thead>
            <tbody>{renderTableRows()}</tbody>
          </table>
        </div>
        <div className="custom-calendar__pagination">
          <TablePagination
            rowsPerPageOptions={[5]}
            component="div"
            count={Math.max(dailyEvents.length, 1)}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} из ${count}`}
            labelRowsPerPage=""
          />
        </div>
      </Paper>
    </div>
  );
};

export default Calendar;
